import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
         AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
         AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CheckCircle2, XOctagon, ShieldAlert } from "lucide-react";

/**
 * GovVerificationActions — Verify / Reject buttons for a gov-scheme appointment.
 *
 * Rendered inline in the receptionist's patient queue view. Automatically
 * disables both buttons the moment either action fires so a receptionist
 * cannot double-submit (protects the atomic-Mongo-transition guarantees).
 *
 * Props:
 *   - appointment: patient row (must include id, name, payment_method,
 *     gov_verified, gov_card_number).
 *   - onDone: callback invoked with the updated appointment row.
 */
export default function GovVerificationActions({ appointment, onDone }) {
  const [busy, setBusy] = useState(false);

  const scheme = (appointment.payment_method || "").toUpperCase();
  const cardMask = appointment.gov_card_number
    ? `${appointment.gov_card_number.slice(0, 4)}${"•".repeat(Math.max(0, appointment.gov_card_number.length - 6))}${appointment.gov_card_number.slice(-2)}`
    : "—";

  const doVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/appointments/${appointment.id}/verify-gov`);
      toast.success(`${scheme} verified — Token #${data.ticket_number || "—"}`);
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
      setBusy(false);
    }
  };

  const doReject = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/appointments/${appointment.id}/reject-gov`);
      toast.success(`${scheme} rejected. Patient notified.`);
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`gov-actions-${appointment.id}`}>
      <Button
        size="sm"
        onClick={doVerify}
        disabled={busy}
        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-2 text-xs"
        data-testid={`gov-verify-btn-${appointment.id}`}
      >
        <CheckCircle2 size={14} className="mr-1" /> Verify
      </Button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 text-white h-8 px-2 text-xs"
            data-testid={`gov-reject-btn-${appointment.id}`}
          >
            <XOctagon size={14} className="mr-1" /> Reject
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-rose-600">
              <ShieldAlert size={20} />
              <AlertDialogTitle>Reject {scheme} card?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-slate-600 pt-2">
              This cancels the appointment for
              <span className="font-medium text-slate-900"> {appointment.name}</span>,
              releases the queue slot, and sends an automated SMS informing them
              their government scheme verification failed. Card: <span className="font-mono">{cardMask}</span>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`gov-reject-cancel-${appointment.id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doReject}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid={`gov-reject-confirm-${appointment.id}`}
            >
              Yes, reject card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
